import { ClientContact } from "@jugl-web/rest-api";
import { Dialog } from "@jugl-web/ui-components";
import { ScreenTransitionWrapper } from "@jugl-web/ui-components/cross-platform/ScreenTransitionWrapper";
import {
  Breadcrumb,
  BreadcrumbsDrawerHeaderProps,
  DrawerHeader,
} from "@jugl-web/ui-components/web/DrawerHeader";
import { useTranslations } from "@jugl-web/utils";
import {
  Screen,
  ScreenTransitionManagerProvider,
  useScreenTransitionManager,
} from "@jugl-web/utils/utils/ScreenTransitionManager";
import {
  ContactDetailsFormValues,
  EMPTY_CONTACT_DETAILS_FORM_VALUES,
} from "@web-src/modules/client-support/components/ContactDetailsFormFields";
import React, { ReactElement, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { ReactComponent as UnknownUserIcon } from "./assets/unknown-user.svg";
import { CreateContactScreen } from "./screens/CreateContactScreen";
import { LinkExistingContactScreen } from "./screens/LinkExistingContactScreen";
import { IdentifyContactDialogScreenMap } from "./types";

interface IdentifyContactDialogProps {
  isOpen: boolean;
  onRequestClose: () => void;
  onContactIdentified?: (contact: ClientContact) => void;
}

const INITIAL_SCREEN: Screen<IdentifyContactDialogScreenMap> = {
  name: "linkExistingContact",
};

const InnerIdentifyContactDialog: React.FC<IdentifyContactDialogProps> = ({
  isOpen,
  onRequestClose,
  onContactIdentified,
}) => {
  const { t } = useTranslations();
  const { screen, renderContent, transitionTo } =
    useScreenTransitionManager<IdentifyContactDialogScreenMap>();
  const [selectedContact, setSelectedContact] = useState<ClientContact>();

  const form = useForm<ContactDetailsFormValues>({
    defaultValues: EMPTY_CONTACT_DETAILS_FORM_VALUES,
  });

  const { reset } = form;

  const header = useMemo<BreadcrumbsDrawerHeaderProps>(() => {
    const unknownUserTitle = t({
      id: "client-support-page.unknown-user",
      defaultMessage: "Unknown User",
    });

    const rootBreadcrumb: Breadcrumb = {
      id: "linkExistingContact",
      title: (
        <div className="flex items-center gap-3">
          <UnknownUserIcon />
          <span>{unknownUserTitle}</span>
        </div>
      ),
      onClick:
        screen.name !== "linkExistingContact"
          ? () => transitionTo({ name: "linkExistingContact" })
          : undefined,
    };

    return renderContent<BreadcrumbsDrawerHeaderProps>({
      linkExistingContact: {
        type: "breadcrumbs",
        breadcrumbs: [rootBreadcrumb],
      },
      createContact: {
        type: "breadcrumbs",
        breadcrumbs: [
          rootBreadcrumb,
          {
            id: "createContact",
            title: t({
              id: "client-support-page.new-customer",
              defaultMessage: "New Customer",
            }),
          },
        ],
      },
    });
  }, [renderContent, screen.name, t, transitionTo]);

  const content = useMemo<ReactElement>(
    () =>
      renderContent({
        linkExistingContact: (
          <LinkExistingContactScreen
            selectedContact={selectedContact}
            onSelectContact={setSelectedContact}
            onRequestClose={onRequestClose}
            onContactIdentified={onContactIdentified}
          />
        ),
        createContact: (
          <CreateContactScreen
            onRequestClose={onRequestClose}
            onContactIdentified={onContactIdentified}
          />
        ),
      }),
    [onContactIdentified, onRequestClose, renderContent, selectedContact]
  );

  useEffect(() => {
    if (isOpen) {
      transitionTo(INITIAL_SCREEN, { force: true });
      setSelectedContact(undefined);
      reset(EMPTY_CONTACT_DETAILS_FORM_VALUES);
    }
  }, [isOpen, reset, transitionTo]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onRequestClose}
      className="flex max-h-[90vh] w-[780px] flex-col rounded-2xl"
    >
      <DrawerHeader {...header} onClose={onRequestClose} />
      <FormProvider {...form}>
        <ScreenTransitionWrapper screenName={screen.name}>
          {content}
        </ScreenTransitionWrapper>
      </FormProvider>
    </Dialog>
  );
};

export const IdentifyContactDialog: React.FC<IdentifyContactDialogProps> = (
  props
) => (
  <ScreenTransitionManagerProvider initialScreen={INITIAL_SCREEN}>
    <InnerIdentifyContactDialog {...props} />
  </ScreenTransitionManagerProvider>
);
